import React, { useState, useEffect } from 'react'

// ── Pill tag input ────────────────────────────────────────
function PillInput({ pills, onChange, placeholder, accent }) {
  const [input, setInput] = useState('')
  const color = accent || 'var(--rust)'

  function handleKeyDown(e) {
    if ((e.key === 'Enter' || e.key === ',') && input.trim()) {
      e.preventDefault()
      const val = input.trim().replace(/,$/, '')
      if (val && !pills.includes(val)) onChange([...pills, val])
      setInput('')
    }
    if (e.key === 'Backspace' && !input && pills.length > 0) {
      onChange(pills.slice(0, -1))
    }
  }

  function remove(pill) {
    onChange(pills.filter(p => p !== pill))
  }

  return (
    <div className="pill-input-wrap">
      {pills.map(pill => (
        <span key={pill} className="pill" style={{ background: color + '18', borderColor: color + '44', color }}>
          {pill}
          <button className="pill-remove" onClick={() => remove(pill)}>✕</button>
        </span>
      ))}
      <input
        className="pill-text-input"
        value={input}
        onChange={e => setInput(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={pills.length === 0 ? placeholder : ''}
      />
    </div>
  )
}

// ── Rubric row ────────────────────────────────────────────
function RubricRow({ rubric, onChange, onDelete }) {
  return (
    <div className="rubric-row">
      <div className="rubric-row-fields">
        <input
          className="editor-input rubric-name-input"
          value={rubric.name}
          onChange={e => onChange({ ...rubric, name: e.target.value })}
          placeholder="Column name"
        />
        <textarea
          className="editor-input rubric-desc-input"
          value={rubric.description}
          onChange={e => onChange({ ...rubric, description: e.target.value })}
          placeholder="What is this column? When should it run?"
          rows={2}
        />
      </div>
      <button className="rubric-delete-btn" onClick={onDelete} title="Remove">✕</button>
    </div>
  )
}

// ── One-off idea row ──────────────────────────────────────
function IdeaRow({ idea, onChange, onDelete }) {
  return (
    <div className="idea-row">
      <input
        className="editor-input"
        value={idea}
        onChange={e => onChange(e.target.value)}
        placeholder="Story idea"
        style={{ flex: 1 }}
      />
      <button className="rubric-delete-btn" onClick={onDelete} title="Remove">✕</button>
    </div>
  )
}

// ── Source row ────────────────────────────────────────────
function SourceRow({ source, onChange, onDelete }) {
  return (
    <div className="idea-row">
      <input
        className="editor-input"
        value={source.url}
        onChange={e => onChange({ ...source, url: e.target.value })}
        placeholder="https://… or site name"
        style={{ flex: 2 }}
      />
      <input
        className="editor-input"
        value={source.note || ''}
        onChange={e => onChange({ ...source, note: e.target.value })}
        placeholder="Note (optional) — e.g. climbing news, check daily"
        style={{ flex: 3 }}
      />
      <button className="rubric-delete-btn" onClick={onDelete} title="Remove">✕</button>
    </div>
  )
}

// ── Section header ────────────────────────────────────────
function SectionHead({ title, sub }) {
  return (
    <div className="instr-section-head">
      <h3 className="instr-section-title">{title}</h3>
      {sub && <p className="instr-section-sub">{sub}</p>}
    </div>
  )
}

// ── Main component ────────────────────────────────────────
export default function Instructions({ onClose }) {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [activeTab, setActiveTab] = useState('voice')

  // Form state
  const [whatThisIs, setWhatThisIs] = useState('')
  const [voice, setVoice] = useState('')
  const [bannedPhrases, setBannedPhrases] = useState([])
  const [sportsAlways, setSportsAlways] = useState([])
  const [sportsConditional, setSportsConditional] = useState([])
  const [sportsNever, setSportsNever] = useState([])
  const [editorialPriorities, setEditorialPriorities] = useState('')
  const [editionStructure, setEditionStructure] = useState('')
  const [writingStyle, setWritingStyle] = useState('')
  const [finalCheck, setFinalCheck] = useState('')
  const [rubricPool, setRubricPool] = useState([])
  const [oneOffIdeas, setOneOffIdeas] = useState([])
  const [criticalEvents, setCriticalEvents] = useState([])
  const [sources, setSources] = useState([])

  useEffect(() => {
    async function load() {
      setLoading(true)
      setError(null)
      try {
        const res = await fetch('/api/instructions-write')
        if (!res.ok) throw new Error('Failed to load instructions')
        const data = await res.json()
        setWhatThisIs(data.what_this_is || '')
        setVoice(data.voice || '')
        setBannedPhrases(data.banned_phrases || [])
        setSportsAlways(data.sports_always || [])
        setSportsConditional(data.sports_conditional || [])
        setSportsNever(data.sports_never || [])
        setEditorialPriorities(data.editorial_priorities || '')
        setEditionStructure(data.edition_structure || '')
        setWritingStyle(data.writing_style || '')
        setFinalCheck(data.final_check || '')
        setRubricPool(data.rubric_pool || [])
        setOneOffIdeas(data.one_off_ideas || [])
        setCriticalEvents(data.critical_events || [])
        setSources(data.sources || [])
      } catch (e) {
        setError(e.message)
      }
      setLoading(false)
    }
    load()
  }, [])

  async function handleSave() {
    setSaving(true)
    setError(null)
    try {
      const res = await fetch('/api/instructions-write', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          what_this_is: whatThisIs,
          voice,
          banned_phrases: bannedPhrases,
          sports_always: sportsAlways,
          sports_conditional: sportsConditional,
          sports_never: sportsNever,
          editorial_priorities: editorialPriorities,
          edition_structure: editionStructure,
          writing_style: writingStyle,
          final_check: finalCheck,
          rubric_pool: rubricPool,
          one_off_ideas: oneOffIdeas,
          critical_events: criticalEvents,
          sources
        })
      })
      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || 'Save failed')
      }
      onClose()
    } catch (e) {
      setError(e.message)
    }
    setSaving(false)
  }

  const TABS = [
    { key: 'voice', label: 'Voice & Style' },
    { key: 'sports', label: 'Sports' },
    { key: 'structure', label: 'Structure' },
    { key: 'columns', label: 'Columns' },
    { key: 'ideas', label: 'Story Ideas' },
    { key: 'events', label: 'Critical Events' },
    { key: 'sources', label: 'Sources' }
  ]

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="instr-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="instr-header">
          <h2 className="imgmgr-title">Editorial Instructions</h2>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            {error && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'calc(0.7rem * var(--scale))', color: '#c0392b' }}>{error}</span>}
            <button className="btn-secondary" onClick={onClose}>Cancel</button>
            <button className="btn-primary" onClick={handleSave} disabled={saving || loading}>
              {saving ? 'Saving…' : 'Save & close'}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="finder-tabs">
          {TABS.map(t => (
            <button key={t.key}
              className={'finder-tab' + (activeTab === t.key ? ' finder-tab--active' : '')}
              onClick={() => setActiveTab(t.key)}
            >{t.label}</button>
          ))}
        </div>

        {loading ? (
          <div className="loading-state"><div className="loading-spinner" /><p>Loading instructions…</p></div>
        ) : (
          <div className="instr-body">

            {/* ── Voice & Style ── */}
            {activeTab === 'voice' && (
              <div className="instr-tab-content">
                <SectionHead title="What this is" sub="A brief description of the publication for Claude's context." />
                <textarea className="editor-input instr-textarea" value={whatThisIs}
                  onChange={e => setWhatThisIs(e.target.value)} rows={4} />

                <SectionHead title="Voice & tone" sub="How Claude should write. Active voice, specific detail, no throat-clearing." />
                <textarea className="editor-input instr-textarea" value={voice}
                  onChange={e => setVoice(e.target.value)} rows={6} />

                <SectionHead title="Banned phrases"
                  sub='Type a phrase and press Enter to add it. These are never used in any output.' />
                <PillInput pills={bannedPhrases} onChange={setBannedPhrases}
                  placeholder="Type a phrase and press Enter…" accent="#c0392b" />

                <SectionHead title="Writing style notes"
                  sub="Additional writing guidance — landscape descriptions, tone for specific sports, etc." />
                <textarea className="editor-input instr-textarea" value={writingStyle}
                  onChange={e => setWritingStyle(e.target.value)} rows={5} />

                <SectionHead title="Final self-check"
                  sub="The checklist Claude runs before outputting each digest." />
                <textarea className="editor-input instr-textarea" value={finalCheck}
                  onChange={e => setFinalCheck(e.target.value)} rows={6} />
              </div>
            )}

            {/* ── Sports ── */}
            {activeTab === 'sports' && (
              <div className="instr-tab-content">
                <SectionHead title="Always in scope"
                  sub="These sports are always covered. Type a sport name and press Enter." />
                <PillInput pills={sportsAlways} onChange={setSportsAlways}
                  placeholder="Type a sport and press Enter…" accent="var(--rust)" />

                <SectionHead title="Conditionally in scope"
                  sub="Covered when remote, new ground being broken, or outside normal competitive structures." />
                <PillInput pills={sportsConditional} onChange={setSportsConditional}
                  placeholder="Type a sport and press Enter…" accent="#C98900" />

                <SectionHead title="Never in scope"
                  sub="These are always excluded regardless of the story." />
                <PillInput pills={sportsNever} onChange={setSportsNever}
                  placeholder="Type a sport and press Enter…" accent="#767676" />
              </div>
            )}

            {/* ── Structure ── */}
            {activeTab === 'structure' && (
              <div className="instr-tab-content">
                <SectionHead title="Editorial priorities"
                  sub="What gets prime billing. Claude follows this order when allocating feature slots." />
                <textarea className="editor-input instr-textarea" value={editorialPriorities}
                  onChange={e => setEditorialPriorities(e.target.value)} rows={8} />

                <SectionHead title="Edition structure"
                  sub="The sections of every edition and what goes in each." />
                <textarea className="editor-input instr-textarea" value={editionStructure}
                  onChange={e => setEditionStructure(e.target.value)} rows={10} />
              </div>
            )}

            {/* ── Columns ── */}
            {activeTab === 'columns' && (
              <div className="instr-tab-content">
                <SectionHead title="Recurring columns"
                  sub="The pool of rubrics Claude can draw from. Name + description tells Claude when and how to use each." />
                <div className="rubric-list">
                  {rubricPool.map((r, i) => (
                    <RubricRow key={i} rubric={r}
                      onChange={updated => setRubricPool(prev => prev.map((x, j) => j === i ? updated : x))}
                      onDelete={() => setRubricPool(prev => prev.filter((_, j) => j !== i))} />
                  ))}
                </div>
                <button className="btn-secondary" style={{ marginTop: '0.75rem' }}
                  onClick={() => setRubricPool(prev => [...prev, { name: '', description: '' }])}>
                  + Add column
                </button>
              </div>
            )}

            {/* ── Story Ideas ── */}
            {activeTab === 'ideas' && (
              <div className="instr-tab-content">
                <SectionHead title="One-off story ideas"
                  sub="A standing list of story ideas Claude can draw from when proposing regular features." />
                <div className="idea-list">
                  {oneOffIdeas.map((idea, i) => (
                    <IdeaRow key={i} idea={idea}
                      onChange={val => setOneOffIdeas(prev => prev.map((x, j) => j === i ? val : x))}
                      onDelete={() => setOneOffIdeas(prev => prev.filter((_, j) => j !== i))} />
                  ))}
                </div>
                <button className="btn-secondary" style={{ marginTop: '0.75rem' }}
                  onClick={() => setOneOffIdeas(prev => [...prev, ''])}>
                  + Add idea
                </button>
              </div>
            )}

            {/* ── Critical Events ── */}
            {activeTab === 'events' && (
              <div className="instr-tab-content">
                <SectionHead title="Critical events"
                  sub="Major events deserving extended multi-edition coverage. Claude will track and prioritise these." />
                <div className="idea-list">
                  {criticalEvents.map((event, i) => (
                    <IdeaRow key={i} idea={event}
                      onChange={val => setCriticalEvents(prev => prev.map((x, j) => j === i ? val : x))}
                      onDelete={() => setCriticalEvents(prev => prev.filter((_, j) => j !== i))} />
                  ))}
                </div>
                <button className="btn-secondary" style={{ marginTop: '0.75rem' }}
                  onClick={() => setCriticalEvents(prev => [...prev, ''])}>
                  + Add event
                </button>
              </div>
            )}

            {/* ── Sources ── */}
            {activeTab === 'sources' && (
              <div className="instr-tab-content">
                <SectionHead title="Preferred sources"
                  sub="Sites the story finder checks first. Add a URL or site name, plus an optional note on what to look for." />
                <div className="idea-list">
                  {sources.map((src, i) => (
                    <SourceRow key={i} source={src}
                      onChange={updated => setSources(prev => prev.map((x, j) => j === i ? updated : x))}
                      onDelete={() => setSources(prev => prev.filter((_, j) => j !== i))} />
                  ))}
                </div>
                <button className="btn-secondary" style={{ marginTop: '0.75rem' }}
                  onClick={() => setSources(prev => [...prev, { url: '', note: '' }])}>
                  + Add source
                </button>
              </div>
            )}

          </div>
        )}
      </div>
    </div>
  )
}
